/**
 * apps/battle/client/src/terminal/screens/ExitNarrativeScreen.tsx
 * Cluster exit narrative — fired the first time the cluster enters clearedSet.
 * Lists newly-unlocked clusters and newly-available portals.
 * Auto-advances after 8 s or on [Enter].
 */
import { useEffect, useState } from 'react';
import { TerminalFrame } from '../components/TerminalFrame.js';
import { KeyBar, type KeyBarItem } from '../components/KeyBar.js';
import { useKeyboard } from '../hooks/useKeyboard.js';
import { CLUSTER_NARRATIVE, type ClusterId } from '../narrative.js';
import { NARRATIVE_AUTO_ADVANCE_MS } from '../config.js';
import type { ClusterState } from '../../api.js';
import type { UnlockDiff } from '../state/unlockDiff.js';

interface Props {
  cluster: ClusterState;
  xpAwarded: number;
  diff: UnlockDiff;
  onContinue: () => void;
}

export function ExitNarrativeScreen({ cluster, xpAwarded, diff, onContinue }: Props) {
  const id = cluster.clusterId as ClusterId;
  const n = CLUSTER_NARRATIVE[id];
  const [countdown, setCountdown] = useState(Math.round(NARRATIVE_AUTO_ADVANCE_MS / 1000));

  useEffect(() => {
    const t = setTimeout(onContinue, NARRATIVE_AUTO_ADVANCE_MS);
    const i = setInterval(() => setCountdown((c) => Math.max(0, c - 1)), 1000);
    return () => { clearTimeout(t); clearInterval(i); };
  }, [onContinue]);

  useKeyboard([{ key: 'Enter', handler: onContinue }]);

  const title = (
    <>
      ECONQUEST <span className="tm-text--dim">{'\u2500'}</span> CLUSTER {cluster.clusterId}{' '}
      <span className="tm-text--dim">{'\u2500'}</span>{' '}
      <span className="tm-text--accent">SEALED</span>
    </>
  );

  const keybar: KeyBarItem[] = [
    { label: 'Enter', description: 'continue', onActivate: onContinue },
  ];

  const newClusters = diff.newlyUnlockedClusters.slice(0, 3);
  const newPortals = diff.newlyAvailablePortals.slice(0, 3);

  return (
    <TerminalFrame title={title} keybar={<KeyBar items={keybar} />}>
      <div className="tm-narrative">
        <div className="tm-h1">{n?.name ?? cluster.fullName}</div>
        {n?.exit ?? `Cluster ${cluster.clusterId} sealed.`}

        {xpAwarded > 0 && (
          <div className="tm-narrative__unlock">
            +{xpAwarded} XP earned.
          </div>
        )}

        {newClusters.map((c) => (
          <div key={c.clusterId} className="tm-narrative__unlock">
            Cluster {c.clusterId} — {c.name} — is now unlocked.
          </div>
        ))}

        {newPortals.map((p) => (
          <div key={p.portalKey} className="tm-narrative__unlock tm-narrative__unlock--portal">
            {'\u26A1'} A new portal shimmers: {p.portalKey} ({p.tier}-mark).
          </div>
        ))}

        {diff.newlyUnlockedClusters.length > 3 && (
          <div className="tm-narrative__unlock">
            …and {diff.newlyUnlockedClusters.length - 3} more cluster(s).
          </div>
        )}
        {diff.newlyAvailablePortals.length > 3 && (
          <div className="tm-narrative__unlock tm-narrative__unlock--portal">
            …and {diff.newlyAvailablePortals.length - 3} more portal(s).
          </div>
        )}

        <div className="tm-narrative__sub">
          {'>'} auto-advance in {countdown}s
        </div>
      </div>
    </TerminalFrame>
  );
}
